import React, { useCallback } from 'react';
import styled from 'styled-components/native';

import { memo } from '@webtrader/core/constants/react';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { TTabLabelType } from '@webtrader/core/utils/tab';

import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

export interface IBaseTabOption<T> {
  label: TTabLabelType;
  value: T;
  hidden?: boolean;
  testID?: EDataTestId;
}

interface IProps<T> {
  option: IBaseTabOption<T>;
  fontSize: number;
  selected?: boolean;
  underlineSelected?: boolean;
  enableReselect?: boolean;
  onChange?: (value: T) => void;
}

type TabOptionComponentProps = <T>(props: IProps<T>) => JSX.Element;

const TabOptionSelector: TabOptionComponentProps = (props) => {
  const { option, fontSize, selected, underlineSelected, enableReselect, onChange } = props;
  const { label, value: optionValue, hidden, testID } = option;

  const onSelect = useCallback(() => {
    if (enableReselect && onChange) {
      onChange(optionValue);
      return;
    }

    if (!selected && onChange) {
      onChange(optionValue);
    }
  }, [onChange, optionValue, selected, enableReselect]);

  if (hidden) {
    return <></>;
  }

  return (
    <TabOptionItem
      selected={selected}
      activeOpacity={1}
      underlineSelected={underlineSelected}
      onPress={onSelect}
      testID={testID}
      accessibilityState={{ selected }}
    >
      <TabOptionItemText fontSize={fontSize} selected={selected}>
        {label instanceof Function ? label() : label}
      </TabOptionItemText>
    </TabOptionItem>
  );
};

export default memo(TabOptionSelector);

export const TabOptionItem = styled(TouchableOpacity)<{ selected?: boolean; underlineSelected?: boolean }>`
  margin-end: ${dynamicSize.convertSize(18)}px;

  justify-content: center;

  border-bottom-width: ${({ underlineSelected }) => (underlineSelected ? 2 : 0)}px;
  border-bottom-color: ${({ theme, selected }) => (selected ? theme.colors.primary : 'transparent')};
`;

export const TabOptionItemText = styled(Text)<{ fontSize: number; selected?: boolean }>`
  color: ${({ theme, selected }) => (selected ? theme.colors.primary : theme.colors.text[0])};
  font-size: ${({ fontSize }) => `${dynamicSize.convertSize(fontSize)}px`};
  font-weight: 500;
`;
